import { Dropdown, Col, Row, Space, Typography, Modal, message } from "antd";
import { useState } from "react";
import { Guid } from "guid-typescript";
import { SearchQuery } from "../code/search-query/search-query";
import { getRoadmapsExport, activateRoadmap } from "../repository/data.repository";
import { getProblemMessage } from "../utils/http";
import { Roadmap } from "../repository/data.default";
import Loader from "../controls/Loader";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import RoadmapsView from "../components/RoadmapsView";
import RoadmapView from "../components/RoadmapView";
import RoadmapManage from "../components/processes/RoadmapManage";

// The journey-authoring surface: a flat grid of roadmaps + a purpose-built Manage builder per roadmap.
function RoadmapsPage() {
  const [modal, contextHolder] = Modal.useModal();
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({ conditions: [] });
  const [inputMode, setInputMode] = useState<"add" | "delete">("add");
  const [inputOpen, setInputOpen] = useState(false);
  const [inputUid, setInputUid] = useState<string | null>(null);
  const [manageUid, setManageUid] = useState<string | null>(null);
  const [triggerView, setTriggerView] = useState(Guid.create().toString());
  const [activating, setActivating] = useState(false);
  const permissions = useSelector(authorizeSelectors.permissions);

  function refresh(): void {
    setTriggerView(Guid.create().toString());
  }

  function onSearch(searchQuery: SearchQuery): void {
    setSearchQuery(searchQuery);
  }

  function onAction(key: string, record: any): void {
    if (key === "manage") {
      setManageUid(record.uid);
      return;
    }
    if (key === "activate") {
      onActivate(record);
      return;
    }
    // delete
    setInputMode("delete");
    setInputUid(record.uid);
    setInputOpen(true);
  }

  // Activation is platform-wide (swaps the single live journey for ALL companies) — confirm + guard.
  function onActivate(record: any): void {
    modal.confirm({
      title: "Make this the active roadmap?",
      content: `"${record.name}" becomes the single live journey for ALL companies, and the currently active roadmap is deactivated. In-progress companies will follow this roadmap from here.`,
      okText: "Make Active",
      centered: true,
      onOk: () => {
        if (activating) return;
        setActivating(true);
        return activateRoadmap(record.uid)
          .then(refresh)
          .catch(() => message.error("Couldn't activate the roadmap — please retry."))
          .finally(() => setActivating(false));
      },
    });
  }

  function onAdd(): void {
    setInputMode("add");
    setInputUid(null);
    setInputOpen(true);
  }

  function onInputFinish(created?: Roadmap): void {
    setInputOpen(false);
    refresh();
    // Jump straight into the builder for a freshly created roadmap.
    if (inputMode === "add" && created?.uid) {
      setManageUid(created.uid);
    }
  }

  function onInputClose(): void {
    setInputOpen(false);
  }

  function onManageClose(): void {
    setManageUid(null);
    refresh();
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    getRoadmapsExport({
      searchQuery,
      pageNumber: 0,
      pageSize: Number.MAX_SAFE_INTEGER,
    })
      .then(() => loading.destroy())
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Roadmaps
        </Typography.Title>
        <Space className="flex flex-row">
          <Dropdown.Button
            type="primary"
            className={`${!permissions.can("Processes", "create") ? "disabled" : ""}`}
            disabled={!permissions.can("Processes", "create")}
            menu={{
              items: [
                {
                  key: "downloadExport",
                  label: "Export to Excel",
                  disabled: !permissions.can("Processes", "view"),
                  onClick: onDownloadExport,
                },
              ],
            }}
            onClick={onAdd}
          >
            Add Roadmap
          </Dropdown.Button>
        </Space>
      </Row>
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        <RoadmapsView trigger={triggerView} onAction={onAction} onSearch={onSearch} />
      </Col>

      <RoadmapView
        key={inputMode}
        mode={inputMode}
        open={inputOpen}
        uid={inputUid}
        onFinish={onInputFinish}
        onClose={onInputClose}
      />
      <RoadmapManage
        open={!!manageUid}
        uid={manageUid}
        onClose={onManageClose}
      />
      {contextHolder}
    </Col>
  );
}

export default RoadmapsPage;
